import { Component, computed, effect, inject, signal, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import environment from '../../environments';

interface Plano {
  id: string;
  nome: string;
  preco: string;
}

export interface PixResponse {
  empresa: string;
  cnpj: string;
  valor: number;
  comentario: string;
  copiaECola: string;
  qrCodeBase64: string;
}

@Component({
  selector: 'app-registro',
  standalone: true,
  imports: [CommonModule, FormsModule, HttpClientModule],
  templateUrl: './registro.component.html',
  styleUrls: ['./registro.component.css']
})
export class RegistroComponent {
  // Atalho: permitir avançar com Enter em qualquer etapa
  @HostListener('document:keydown.enter', ['$event'])
  onEnterKey(e: Event) {
    // Evita comportamento inesperado em elementos com atributo data-no-enter
    const target = e.target as HTMLElement | null;
    if (target && target.closest('[data-no-enter]')) return;
    // Não faz nada após finalização
    if (this.passo() >= 5) return;
    // Deixa as validações existentes decidirem se pode avançar
    this.avancar();
  }
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);

  enviando = signal<boolean>(false);
  erroEnvio = signal<string | null>(null);

  // Estados de verificação de e-mail (passo 2)
  verificandoEmail = signal<boolean>(false);
  erroEmail = signal<string | null>(null);

  // Estados de verificação de CPF (passo 6)
  verificandoCpf = signal<boolean>(false);
  erroCpf = signal<string | null>(null);

  passo = signal<number>(1);
  planosCarregando = signal<boolean>(true);
  erroPlanos = signal<string | null>(null);

  email = signal<string>('');
  nome = signal<string>('');
  senha = signal<string>('');
  confirmarSenha = signal<string>('');
  cpf = signal<string>('');

  planos: Plano[] = [];

  planoSelecionado = signal<string>('');
  checkoutStatus = signal<'success' | 'error' | null>(null);
  pixDados = signal<PixResponse | null>(null);
  pixCopiado = signal<boolean>(false);

  // Validações simples
  get podeAvancarEmail() {
    const v = this.email();
    return /.+@.+\..+/.test(v);
  }
  get podeAvancarNome() {
    return this.nome().trim().length >= 2;
  }
  get podeAvancarSenha() {
    const v = this.senha();
    return v.trim().length >= 6;
  }
  get senhasConferem() {
    return this.confirmarSenha().length > 0 && this.senha() === this.confirmarSenha();
  }
  get podeAvancarConfirmarSenha() {
    return this.podeAvancarSenha && this.senhasConferem;
  }
  get podeAvancarCPF() {
    const v = this.cpf().replace(/\D/g, '');
    if (v.length === 11) {
      return this.validarCPF(v);
    }
    if (v.length === 14) {
      return this.validarCNPJ(v);
    }
    return false;
  }
  get podeAvancarPlano() {
    return !!this.planoSelecionado();
  }

  get planoAtual(): Plano | undefined {
    return this.planos.find(p => p.id === this.planoSelecionado());
  }

  get dadosValidos() {
    return this.podeAvancarEmail && this.podeAvancarNome &&
      this.podeAvancarConfirmarSenha && this.podeAvancarCPF;
  }

  constructor() {
    // Observa query params para identificar o retorno do checkout
    this.route.queryParamMap.subscribe((params) => {
      const status = params.get('status');
      if (status === 'success' || status === 'error') {
        this.checkoutStatus.set(status);
        this.passo.set(5); // Finalização após retorno de uma futura confirmação de pagamento
      }
    });

    this.configurarValidacaoCpfCnpj();
    this.carregarPlanos();
    this.configurarAutoFocus();
  }

  // Formata preço para BRL com prefixo "R$" e duas casas decimais
  formatPreco(valor: string | number | null | undefined): string {
    if (valor === null || valor === undefined) return 'R$ 0,00';
    let num: number | null = null;

    if (typeof valor === 'number') {
      num = Number.isFinite(valor) ? valor : null;
    } else {
      const raw = String(valor).trim();
      if (!raw) return 'R$ 0,00';
      // normaliza separadores: remove espaços, troca vírgula por ponto para parse
      const normalized = raw
        .replace(/\s+/g, '')
        .replace(/\.(?=\d{3}(\D|$))/g, '') // remove separador de milhar
        .replace(',', '.');
      const parsed = Number.parseFloat(normalized);
      num = Number.isFinite(parsed) ? parsed : null;
    }

    if (num === null) return 'R$ 0,00';

    // Garante duas casas decimais e vírgula como separador
    const fixed = num.toFixed(2);
    const br = fixed.replace('.', ',');
    return `R$ ${br}`;
  }

  avancar() {
    const atual = this.passo();

    if (atual === 1) {
      if (!this.dadosValidos || this.verificandoEmail() || this.verificandoCpf()) return;
      this.verificarEmailNoBackend();
      return;
    }

    if (atual === 2 && this.podeAvancarPlano) {
      this.passo.set(3);
    } else if (atual === 3) {
      this.preCheckoutEnviarDados();
    }
  }

  voltar() {
    const atual = this.passo();
    if (atual === 4) {
      this.passo.set(3);
      return;
    }
    if (atual > 1 && atual <= 3) {
      this.passo.set(atual - 1);
    }
  }

  selecionarPlano(id: string) {
    this.planoSelecionado.set(id);
  }

  async copiarPix() {
    const codigo = this.pixDados()?.copiaECola;
    if (!codigo) return;
    await navigator.clipboard.writeText(codigo);
    this.pixCopiado.set(true);
    setTimeout(() => this.pixCopiado.set(false), 2500);
  }

  private verificarEmailNoBackend() {
    const email = this.email().trim();
    if (!/.+@.+\..+/.test(email)) return;

    const base = String(environment?.apiBaseUrl || (window as any)?.__API_BASE__ || '').trim();
    const URLBase = base ? base.replace(/\/$/, '') : '';
    const tipoSaas = (environment as any)?.tipoSaas ?? 3;
    const endpoint = `${URLBase}/api/usuario/existeEmail/${tipoSaas}/${email}`;

    this.verificandoEmail.set(true);
    this.erroEmail.set(null);

    this.http.get<any>(endpoint, { observe: 'response' as any }).subscribe({
      next: (res) => {
        this.verificandoEmail.set(false);
        // Se houver corpo booleano, usar; se não houver corpo, considerar 200 como existente
        const body = (res as any)?.body;
        const existeBody = typeof body === 'boolean' ? body : !!(body?.existe ?? body?.exists ?? body?.jaExiste ?? body?.alreadyExists);
        if (existeBody) {
          this.erroEmail.set('Este e-mail já está cadastrado. Faça login ou use outro e-mail.');
        } else {
          this.verificarCpfNoBackend();
        }
      },
      error: (err) => {
        this.verificandoEmail.set(false);
        // 404/204: não existe, pode avançar
        if (err?.status === 404 || err?.status === 204) {
          this.verificarCpfNoBackend();
          return;
        }
        // 409: já existe
        if (err?.status === 409) {
          this.erroEmail.set('Este e-mail já está cadastrado.');
          return;
        }
        this.erroEmail.set('Não foi possível verificar o e-mail agora. Tente novamente.');
      }
    });
  }

  private verificarCpfNoBackend() {
    const cpfCnpj = this.cpf().replace(/\D/g, '');
    if (!(cpfCnpj.length === 11 || cpfCnpj.length === 14)) return;

    const base = String(environment?.apiBaseUrl || (window as any)?.__API_BASE__ || '').trim();
    const URLBase = base ? base.replace(/\/$/, '') : '';
    const tipoSaas = (environment as any)?.tipoSaas ?? 3;
    const endpoint = `${URLBase}/api/usuario/existeCpfCnpj/${encodeURIComponent(String(tipoSaas))}/${encodeURIComponent(String(cpfCnpj))}`;

    this.verificandoCpf.set(true);
    this.erroCpf.set(null);

    this.http.get<any>(endpoint, { observe: 'response' as any }).subscribe({
      next: (res) => {
        this.verificandoCpf.set(false);
        const body = (res as any)?.body;
        const existeBody = typeof body === 'boolean' ? body : !!(body?.existe ?? body?.exists ?? body?.jaExiste ?? body?.alreadyExists);
        if (existeBody) {
          this.erroCpf.set('Este CPF/CNPJ já está cadastrado.');
        } else {
          this.passo.set(2);
        }
      },
      error: (err) => {
        this.verificandoCpf.set(false);
        // 404/204: não existe, pode avançar
        if (err?.status === 404 || err?.status === 204) {
          this.passo.set(2);
          return;
        }
        // 409: já existe
        if (err?.status === 409) {
          this.erroCpf.set('Este CPF/CNPJ já está cadastrado.');
          return;
        }
        this.erroCpf.set('Não foi possível verificar o CPF/CNPJ agora. Tente novamente.');
      }
    });
  }

  private configurarValidacaoCpfCnpj() {
    effect(() => {
      const digitos = this.cpf().replace(/\D/g, '');
      // Se o usuário está digitando, mostramos erro de formato/checagem quando completar o tamanho
      if (digitos.length === 11) {
        const ok = this.validarCPF(digitos);
        this.erroCpf.set(ok ? null : 'CPF inválido');
      } else if (digitos.length === 14) {
        const ok = this.validarCNPJ(digitos);
        this.erroCpf.set(ok ? null : 'CNPJ inválido');
      } else {
        // Enquanto não completou, não exibimos erro
        this.erroCpf.set(null);
      }
    });
  }

  private validarCPF(cpf: string): boolean {
    // Remove sequências repetidas conhecidas e valida tamanho
    if (!cpf || cpf.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(cpf)) return false; // todos os dígitos iguais

    const calcDV = (base: string, factor: number) => {
      let sum = 0;
      for (let i = 0; i < base.length; i++) {
        sum += parseInt(base.charAt(i), 10) * (factor - i);
      }
      const mod = sum % 11;
      return mod < 2 ? 0 : 11 - mod;
    };

    const dv1 = calcDV(cpf.substring(0, 9), 10);
    const dv2 = calcDV(cpf.substring(0, 9) + String(dv1), 11);

    return cpf.endsWith(String(dv1) + String(dv2));
  }

  private validarCNPJ(cnpj: string): boolean {
    if (!cnpj || cnpj.length !== 14) return false;
    if (/^(\d)\1{13}$/.test(cnpj)) return false; // todos os dígitos iguais

    const calcDV = (base: string) => {
      const pesos = [6,5,4,3,2,9,8,7,6,5,4,3,2];
      let sum = 0;
      for (let i = 0; i < base.length; i++) {
        sum += parseInt(base.charAt(i), 10) * pesos[i + (pesos.length - base.length)];
      }
      const mod = sum % 11;
      return mod < 2 ? 0 : 11 - mod;
    };

    const base12 = cnpj.substring(0, 12);
    const dv1 = calcDV(base12);
    const dv2 = calcDV(base12 + String(dv1));

    return cnpj.endsWith(String(dv1) + String(dv2));
  }

  private configurarAutoFocus() {
    effect(() => {
      const p = this.passo();
      setTimeout(() => this.focusFieldForStep(p), 0);
    });
  }

  private focusFieldForStep(step: number) {
    let el: HTMLElement | null = null;
    switch (step) {
      case 1:
        el = document.getElementById('email');
        break;
      case 2:
        el = document.querySelector('.plano') as HTMLElement | null;
        break;
      case 3:
      case 4:
      case 5:
        el = document.querySelector('section.card button') as HTMLElement | null;
        break;
      default:
        // passo 1 (intro) não tem campo de edição. Opcional: focar primeiro botão.
        el = document.querySelector('section.card button') as HTMLElement | null;
    }
    if (el) {
      try {
        el.focus();
        if ((el as HTMLInputElement).select) {
          (el as HTMLInputElement).select();
        }
      } catch {}
    }
  }

  private preCheckoutEnviarDados() {
    // Limpa dados anteriores do PIX antes de tentar novamente
    this.pixDados.set(null);
    const base = String(environment?.apiBaseUrl || (window as any)?.__API_BASE__ || '').trim();
    const URLBase = base ? base.replace(/\/$/, '') : '';
    const endpoint = `${URLBase}/api/Registro`;

    const cpfNum = this.cpf().replace(/\D/g, '');
    const planoIdRaw = this.planoSelecionado();
    const planoNum = Number.parseInt(String(planoIdRaw), 10);
    if (!Number.isFinite(planoNum) || Number.isNaN(planoNum) || planoNum <= 0) {
      this.erroEnvio.set('Selecione novamente um plano válido.');
      this.passo.set(2);
      return;
    }
    const plano = planoNum;

    const tipoSaas = (environment as any)?.tipoSaas ?? 3;

    const payload: any = {
      cnpj: cpfNum.length === 14 ? cpfNum : '',
      cpf: cpfNum.length === 11 ? cpfNum : '',
      email: this.email().trim(),
      logo: '',
      nomeAdvogado: this.nome().trim(),
      responsavel: '',
      razaoSocial: '',
      plano: plano,
      usuario: {
        email: this.email().trim(),
        nome: this.nome().trim(),
        senha: this.senha(),
        confirmacaoSenha: this.confirmarSenha(),
        tipo: 1,
        origemId: null,
        usuarioResponsavel: true,
        tipoSaas: tipoSaas
      },
      tipoSaas: tipoSaas
    };

    this.enviando.set(true);
    this.erroEnvio.set(null);

    this.http.post<any>(endpoint, payload).subscribe({
      next: (res) => {
        this.enviando.set(false);

        // Verifica se a resposta é um objeto PixResponse
        if (res && res.qrCodeBase64) {
          this.pixDados.set(res as PixResponse);
          this.passo.set(4);
          return;
        }

        if (typeof res === 'string') {
          const txt = res.trim().toLowerCase();
          if (txt === 'ok' || txt === 'registro' || txt === 'sucesso' || txt === 'success') {
            this.checkoutStatus.set('success');
            this.passo.set(5);
            return;
          }
          // Caso o texto pareça uma URL, tentar redirecionar
          try {
            const parsed = new URL(res, window.location.origin);
            window.location.href = parsed.href;
            return;
          } catch {}
          // Texto não reconhecido: considerar sucesso padrão
          this.checkoutStatus.set('success');
          this.passo.set(5);
          return;
        }

        // Se vier objeto com URL (compatibilidade com comportamento anterior)
        const url = (res?.url || res?.checkoutUrl || '').toString();
        try {
          if (!url) {
            // Sem URL: avançar para confirmação
            this.checkoutStatus.set('success');
            this.passo.set(5);
            return;
          }
          const parsed = new URL(url, window.location.origin);
          const finalUrl = parsed.href;
          window.location.href = finalUrl;
        } catch (e) {
          this.erroEnvio.set('Não foi possível iniciar o checkout: URL inválida retornada pelo servidor.');
        }
      },
      error: (err) => {
        this.enviando.set(false);
        // Se o erro for que o servidor retornou texto puro mas o HttpClient esperava JSON
        if (err.status === 200 && typeof err.error?.text === 'string') {
          const res = err.error.text;
          const txt = res.trim().toLowerCase();
          if (txt === 'ok' || txt === 'registro' || txt === 'sucesso' || txt === 'success') {
            this.checkoutStatus.set('success');
            this.passo.set(5);
            return;
          }
          try {
            const parsed = new URL(res, window.location.origin);
            window.location.href = parsed.href;
            return;
          } catch {}
          this.checkoutStatus.set('success');
          this.passo.set(5);
          return;
        }
        const mensagem = typeof err?.error === 'string'
          ? err.error
          : err?.error?.message || err?.error?.title;
        this.erroEnvio.set(mensagem || 'Não foi possível enviar seus dados. Tente novamente.');
      }
    });
  }

  isAtivo(n: number) {
    return this.passo() === n;
  }

  private carregarPlanos() {
    const base = String(environment?.apiBaseUrl || (window as any)?.__API_BASE__ || '').trim();
    const URLBase = base ? base.replace(/\/$/, '') : '';
    const tipoSaas = (environment as any)?.tipoSaas ?? 3;
    const endpoint = `${URLBase}/api/plano?tipoSaas=${encodeURIComponent(String(tipoSaas))}`;

    this.http.get<Plano[] | any>(endpoint).subscribe({
      next: (lista) => {
        this.planosCarregando.set(false);
        const arr = Array.isArray(lista) ? lista : [];
        const normalized = arr.map((p: any) => ({
          id: String(p.id ?? p.codigo ?? p.slug ?? ''),
          nome: String(p.nome ?? p.name ?? ''),
          preco: String(p.preco ?? p.price ?? p.valor ?? ''),
        })).filter(p => p.id && p.nome);
        this.planos = normalized;
        this.erroPlanos.set(this.planos.length ? null : 'Nenhum plano está disponível no momento.');
        if (this.planos.length === 1) {
          this.planoSelecionado.set(this.planos[0].id);
        }
      },
      error: (err) => {
        console.error('Falha ao carregar planos', err);
        this.planosCarregando.set(false);
        this.planos = [];
        this.erroPlanos.set('Não foi possível carregar os planos. Tente novamente.');
      }
    });
  }
}
